<script setup>
/**
 * ValidationMarker.vue
 *
 * The validation marker in a flow node's header: an icon shown while the validator
 * has flagged the node (`data.__validationError`), with the validator's message as
 * its tooltip. Shared by every node type; each node still draws its own red (or
 * yellow) border.
 *
 * Red `error_outline` by default. A finding the validator marks with
 * `severity: 'warning'` does not invalidate the flow and must not read as an
 * error, so it gets a yellow `warning_amber` instead (today only ApiCall emits
 * one).
 *
 * A v-tooltip is not exposed to screen readers, so the icon is the accessible
 * marker itself: `role="img"` + `aria-label` carry the same message (same pattern
 * as ExitNodeBadge).
 *
 * @example
 * <ValidationMarker :error="data.__validationError" />
 *
 * @prop {Object} error - The node's `data.__validationError` ({ message, severity? }).
 *                        null (the default) renders nothing.
 */

import { computed } from 'vue';

const props = defineProps({
  /** The node's `data.__validationError`: `{ message, severity? }`, or null. */
  error: {
    type: Object,
    default: null
  }
});

const isWarning = computed(() => props.error?.severity === 'warning');
</script>

<template>
  <span
    v-if="error"
    :class="['material-icons text-base select-none', isWarning ? 'text-yellow-400' : 'text-red-400']"
    role="img"
    :aria-label="error.message"
    v-tooltip="error.message"
  >{{ isWarning ? 'warning_amber' : 'error_outline' }}</span>
</template>
